import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { AmplitudeProvider } from './providers/AmplitudeProvider';
import { AmplitudeExperimentProvider } from './providers/AmplitudeExperimentProvider';
import { FacebookPixelProvider } from './providers/FacebookPixelProvider';
import { GTMProvider } from './providers/GTMProvider';
import {
    configureMarketingTags,
    ensureDataLayer,
    ensureFbqStub,
    loadMarketingTags,
} from './utils/marketingTags';
import { TokenAuthProvider } from './providers/TokenAuthProvider';
import FunnelV3Flow from './components/funnelV3/FunnelV3Flow';
import i18n, { ensureLocale, ENV_DEFAULT_LOCALE } from './i18n';
import { isSupportedLocale, LOCALES, AppLocale } from './config/locales';
import { DisplayCurrencyProvider } from './components/funnelV3/currency/useDisplayCurrency';
import './App.css';

const AMPLITUDE_API_KEY = process.env.REACT_APP_AMPLITUDE_API_KEY || '';
const FB_PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || '';
const FB_PIXEL_ID_2 = process.env.REACT_APP_FB_PIXEL_ID_2 || '';
const GTM_ID = process.env.REACT_APP_GTM_ID || '';

// Configure queues before React mounts. Child effects can emit Meta events
// before provider effects run (for example, a persisted ResultsPage resume).
// GTM + Pixel start immediately so GTM can own PageView (Meta LPV). Do not
// also set fbAutoPageView — that would double-fire vs the GTM Pixel tag.
ensureDataLayer();
ensureFbqStub();
configureMarketingTags({
    gtmId: GTM_ID,
    fbPixelIds: [FB_PIXEL_ID, FB_PIXEL_ID_2].filter(Boolean),
    fbAutoPageView: false,
});
void loadMarketingTags();

// Lazy-load non-critical routes — these are never needed on initial page load
const OnboardingFlow = React.lazy(() => import('./components/onboarding/OnboardingFlow'));
const Funnel2Flow = React.lazy(() => import('./components/funnel2/Funnel2Flow'));
const WalkToEarnFlow = React.lazy(() => import('./components/walkToEarn/WalkToEarnFlow'));
const SocialWalkingFlow = React.lazy(
    () => import('./components/socialWalking/SocialWalkingFlow'),
);
const MoveFitFlow = React.lazy(() => import('./components/movefit/MoveFitFlow'));
const Login = React.lazy(() => import('./components/auth/Login'));
const MembersPage = React.lazy(() => import('./pages/MembersPage'));
const ClubsPage = React.lazy(() => import('./pages/ClubsPage'));
const FitBandPage = React.lazy(() => import('./pages/FitBandPage'));

const RedirectWithQuery = ({ to }: { to: string }) => {
    const location = useLocation();
    return <Navigate to={`${to}${location.search}`} replace />;
};

function AccountRedirect() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const destination = token
        ? `https://account.move-club.co?token=${token}`
        : 'https://account.move-club.co';

    useEffect(() => {
        window.location.replace(destination);
    }, [destination]);

    return <div className="min-h-dvh bg-white" />;
}

const isLocaleReady = (locale: AppLocale): boolean =>
    i18n.hasResourceBundle(locale, 'translation') && i18n.language === locale;

/**
 * Loads the target locale's translations (lazy bundles included) before rendering
 * children, so a localized funnel never flashes the wrong language. Already-active
 * locales render immediately with no flash (keeps the default English funnel fast).
 */
const LocaleGate: React.FC<{ locale: AppLocale; children: React.ReactNode }> = ({ locale, children }) => {
    const [ready, setReady] = useState(() => isLocaleReady(locale));

    useEffect(() => {
        let active = true;
        if (isLocaleReady(locale)) {
            document.documentElement.lang = LOCALES[locale].htmlLang;
            setReady(true);
            return;
        }
        setReady(false);
        ensureLocale(locale).then(() => {
            if (!active) return;
            document.documentElement.lang = LOCALES[locale].htmlLang;
            setReady(true);
        });
        return () => { active = false; };
    }, [locale]);

    if (!ready) return <div className="min-h-dvh bg-white" />;
    return <>{children}</>;
};

/**
 * Renders the V3 funnel under an optional /:lang prefix. Unsupported language
 * segments redirect to the default funnel, preserving the query string so UTM
 * and click-id params are never dropped. Without a prefix, falls back to the
 * build-time default locale (English on the main project, Spanish on the es project).
 */
const LocalizedFunnel: React.FC = () => {
    const { lang } = useParams();
    const location = useLocation();

    if (lang !== undefined && !isSupportedLocale(lang)) {
        return <Navigate to={`/${location.search}`} replace />;
    }

    const locale: AppLocale = isSupportedLocale(lang) ? lang : ENV_DEFAULT_LOCALE;
    return (
        <LocaleGate locale={locale}>
            <DisplayCurrencyProvider>
                <FunnelV3Flow />
            </DisplayCurrencyProvider>
        </LocaleGate>
    );
};

const App: React.FC = () => {
    return (
        <BrowserRouter>
            <AmplitudeProvider apiKey={AMPLITUDE_API_KEY}>
                <AmplitudeExperimentProvider>
                    <FacebookPixelProvider pixelId={FB_PIXEL_ID} pixelId2={FB_PIXEL_ID_2} autoPageView={false}>
                        <GTMProvider gtmId={GTM_ID}>
                            <AuthProvider>
                                <TokenAuthProvider showErrorModal={
                                        window.location.hostname.includes('account') ||
                                        window.location.hostname.includes('admin')
                                    }>
                                    <Suspense fallback={<div className="min-h-dvh bg-white" />}>
                                        <Routes>
                                            {/* Funnel routes */}
                                            <Route path="/" element={<LocalizedFunnel />} />
                                            {/* Discount emails use /checkout. Must be static so :lang does not treat "checkout" as a locale and dump the visit on quiz step 1. */}
                                            <Route path="/checkout" element={<LocalizedFunnel />} />
                                            <Route path="/v2" element={<Funnel2Flow />} />
                                            <Route path="/quiz" element={<LocalizedFunnel />} />
                                            <Route path="/legacy" element={<OnboardingFlow />} />

                                            {/* Walktoearn prize-first fork (Dana) — static routes above :lang/* */}
                                            <Route
                                                path="/walktoearn"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <WalkToEarnFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />
                                            <Route
                                                path="/walktoearn/quiz"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <WalkToEarnFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />

                                            {/* Social Walking — 30-Day Walk Together Challenge */}
                                            <Route
                                                path="/socialwalking"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <SocialWalkingFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />
                                            <Route
                                                path="/socialwalking/quiz"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <SocialWalkingFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />

                                            {/* MoveFit — WalkFit-style walking diagnostic */}
                                            <Route
                                                path="/movefit"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <MoveFitFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />
                                            <Route
                                                path="/movefit/quiz"
                                                element={
                                                    <DisplayCurrencyProvider>
                                                        <MoveFitFlow />
                                                    </DisplayCurrencyProvider>
                                                }
                                            />

                                            {/* Auth */}
                                            <Route path="/login" element={<Login />} />

                                            {/* Admin / User pages */}
                                            <Route path="/account" element={<AccountRedirect />} />
                                            <Route path="/dashboard" element={<MembersPage />} />
                                            <Route path="/members" element={<MembersPage />} />
                                            <Route path="/clubs" element={<ClubsPage />} />
                                            <Route path="/free-band" element={<RedirectWithQuery to="/fitband" />} />
                                            <Route path="/fitband" element={<FitBandPage />} />
                                            <Route path="/user/:userId" element={<AccountRedirect />} />
                                            <Route path="/user/:userId/subscription" element={<AccountRedirect />} />
                                            <Route path="/user/:userId/cancellation" element={<AccountRedirect />} />

                                            {/* Locale-prefixed funnel (e.g. /jp, /es, /jp/quiz). Static routes above rank higher. */}
                                            <Route path=":lang/*" element={<LocalizedFunnel />} />
                                        </Routes>
                                    </Suspense>
                                </TokenAuthProvider>
                            </AuthProvider>
                        </GTMProvider>
                    </FacebookPixelProvider>
                </AmplitudeExperimentProvider>
            </AmplitudeProvider>
        </BrowserRouter>
    );
};

export default App;
